import { ApiResponse } from '../types';
import { API_BASE_URL } from '../config/urls';

export class ApiError extends Error {
    constructor(public status: number, message: string) {
        super(message);
        this.name = 'ApiError';
    }
}

export class ApiClient {
    private baseUrl: string;

    constructor(baseUrl: string = API_BASE_URL) {
        this.baseUrl = baseUrl;
    }

    private getAuthHeaders(): Record<string, string> {
        const sessionToken = localStorage.getItem('sessionToken');
        if (sessionToken) {
            return {
                'Authorization': `Bearer ${sessionToken}`
            };
        }
        return {};
    }

    async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseUrl}${endpoint}`;

        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeaders(),
                ...options.headers,
            },
            ...options,
        });

        const data = await response.json();

        if (!response.ok) {
            throw new ApiError(response.status, data.error || 'An error occurred');
        }

        return data;
    }

    async get<T>(endpoint: string): Promise<T> {
        const response = await fetch(`${this.baseUrl}${endpoint}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...this.getAuthHeaders(),
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
            throw new ApiError(response.status, errorData.error || 'Request failed');
        }

        return response.json();
    }

    async post<T>(endpoint: string, data: any): Promise<T> {
        const response = await this.request<T>(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        });

        return response.data as T;
    }

    async put<T>(endpoint: string, data: any): Promise<T> {
        const response = await this.request<T>(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data),
        });

        return response.data as T;
    }

    async delete<T>(endpoint: string): Promise<void> {
        await this.request<T>(endpoint, {
            method: 'DELETE',
        });
    }
}

export const apiClient = new ApiClient();
